if (!customElements.get('artesta-subscriber-bar')) {
  customElements.define('artesta-subscriber-bar', class extends HTMLElement {
    connectedCallback() {
      this.openButton = this.querySelector('[data-subscriber-open]');
      this.dialog = this.querySelector('dialog');
      this.status = this.querySelector('[data-subscriber-status]');
      this.inlineContainer = this.querySelector('[data-subscriber-form]');
      if (!this.openButton || !this.dialog || !this.inlineContainer) return;
      this.controller = new AbortController();
      this.heightInitialized = false;
      const options = {signal: this.controller.signal};
      this.openButton.addEventListener('click', () => {
        if (this.hidden || this.dialog.open) return;
        this.dialog.showModal();
        this.openButton.setAttribute('aria-expanded', 'true');
        this.openedAt = Date.now();
        this.sync();
      }, options);
      this.querySelector('[data-subscriber-close]')?.addEventListener('click', () => this.dialog.close(), options);
      this.dialog.addEventListener('close', () => {
        clearTimeout(this.waitTimer);
        this.openButton.setAttribute('aria-expanded', 'false');
        if (this.openButton.isConnected && !this.hidden) this.openButton.focus({preventScroll: true});
      }, options);
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) {
          this.dialog.close();
        }
      }, options);
      this.schedule = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {this.frame = 0; this.sync();});
      };
      this.section = this.closest('.shopify-section');
      this.promotion = this.section?.querySelector('artesta-promotion-banner');
      this.promotionObserver = new MutationObserver(this.schedule);
      if (this.promotion) this.promotionObserver.observe(this.promotion, {attributes: true, attributeFilter: ['hidden']});
      // Product grids and other apps mutate the body frequently. Only Forms or
      // promotion mount changes can alter the roots this component needs.
      this.documentObserver = new MutationObserver(records => {
        const relevant = 'shopify-forms-embed, artesta-promotion-banner, [id^="app-embed-container-"]';
        for (const record of records) {
          for (const node of [...record.addedNodes, ...record.removedNodes]) {
            if (node.nodeType !== 1) continue;
            if (node.matches(relevant) || node.querySelector(relevant)) {
              this.schedule();
              return;
            }
          }
        }
      });
      this.documentObserver.observe(document.body, {childList: true, subtree: true});
      this.shadowObserver = new MutationObserver(this.schedule);
      if (typeof ResizeObserver === 'function' && this.section) {
        this.resizeObserver = new ResizeObserver(() => this.updateHeight());
        this.resizeObserver.observe(this.section);
      }
      this.sync();
    }

    updateHeight() {
      const height = this.section?.getBoundingClientRect().height || 0;
      const value = height ? `${height}px` : '';
      if (document.body.style.getPropertyValue('--artesta-marketing-bar-height') === value) return;
      if (value) document.body.style.setProperty('--artesta-marketing-bar-height', value);
      else document.body.style.removeProperty('--artesta-marketing-bar-height');
    }

    observeRoots(roots) {
      if (roots.length === this.roots?.length && roots.every((root, index) => root === this.roots[index])) return;
      this.shadowObserver.disconnect();
      this.roots = roots;
      for (const root of roots) this.shadowObserver.observe(root, {
        childList: true, subtree: true, attributes: true, attributeFilter: ['data-current-step'],
      });
    }

    sync() {
      if (!this.isConnected) return;
      clearTimeout(this.waitTimer);
      const promotion = this.section?.querySelector('artesta-promotion-banner');
      const promotionChanged = promotion !== this.promotion;
      if (promotionChanged) {
        this.promotionObserver.disconnect();
        this.promotion = promotion;
        if (promotion) this.promotionObserver.observe(promotion, {attributes: true, attributeFilter: ['hidden']});
      }
      const wasHidden = this.hidden;
      this.hidden = this.dataset.priceState !== 'base' || Boolean(this.promotion && !this.promotion.hidden);
      if (this.hidden && this.dialog.open) this.dialog.close();
      if (!this.heightInitialized || promotionChanged || wasHidden !== this.hidden) {
        this.updateHeight();
        this.heightInitialized = true;
      }
      const widgets = [...this.inlineContainer.querySelectorAll('shopify-forms-embed')];
      const roots = widgets.map(widget => widget.shadowRoot).filter(Boolean);
      const original = this.dataset.popupId && document.getElementById(`app-embed-container-${this.dataset.popupId}`);
      this.observeRoots([...roots, ...(original?.shadowRoot ? [original.shadowRoot] : [])]);
      const ready = roots.some(root => root.querySelector('[data-form-root][data-current-step="form"] form, [data-form-root][data-current-step="success"]'));
      // Suppress only the old teaser once its replacement has actually loaded.
      if (ready && original?.shadowRoot && !this.teaserStyle?.isConnected) {
        this.teaserStyle = document.createElement('style');
        this.teaserStyle.dataset.artestaSubscriberStyle = '';
        this.teaserStyle.textContent = '[data-form-root][data-current-step="tease"] {display:none!important}';
        original.shadowRoot.append(this.teaserStyle);
      }
      if (!ready) {
        this.teaserStyle?.remove();
        this.teaserStyle = null;
      }
      const validId = /^[1-9]\d*$/.test(this.dataset.formId || '') && this.dataset.formId !== this.dataset.popupId;
      const timedOut = this.dialog.open && Date.now() - this.openedAt >= 15000;
      this.inlineContainer.setAttribute('aria-busy', String(!ready && validId && !timedOut));
      if (this.status) {
        const text = !validId || timedOut ? this.dataset.loadError : this.dataset.loading;
        this.status.hidden = ready;
        if (!ready && this.status.textContent !== text) this.status.textContent = text || '';
      }
      if (this.dialog.open && !ready && validId && !timedOut) this.waitTimer = setTimeout(this.schedule, 250);
    }

    disconnectedCallback() {
      this.controller?.abort();
      clearTimeout(this.waitTimer);
      cancelAnimationFrame(this.frame);
      this.frame = 0;
      this.resizeObserver?.disconnect();
      this.promotionObserver?.disconnect();
      this.documentObserver?.disconnect();
      this.shadowObserver?.disconnect();
      this.roots = [];
      this.teaserStyle?.remove();
      this.teaserStyle = null;
      if (this.dialog?.open) this.dialog.close();
      document.body.style.removeProperty('--artesta-marketing-bar-height');
    }
  });
}
